import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, getDocs, query, collection, where, limit } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import { useAuth } from '../components/auth/AuthContext';
import Toast from '../components/common/Toast';
import { usePermissions } from '../hooks/usePermissions';
import { FEATURE_PERMISSIONS, getRbacMatchNames } from '../utils/permissions';
import { monitorQuery } from '../utils/queryMonitoring';
import { formatDate as formatDateHelper } from '../utils/dateHelpers';

const OrderDetailPage = () => {
    const { orderid } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { can } = usePermissions(currentUser);
    
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const [isAuthorized, setIsAuthorized] = useState(true); // Track authorization status

    // Fetch order details with authorization check
    useEffect(() => {
        if (!currentUser || !orderid) return;
        
        const fetchOrder = async () => {
            try {
                setLoading(true);
                console.log('Fetching order with ID:', orderid);
                console.log('Collection path:', collectionPath('orders'));
                console.log('Current user:', currentUser.email, 'Role:', currentUser.role, 'DisplayName:', currentUser.displayName);
                
                const orderDoc = await monitorQuery('fetch-order-detail', () => 
                    getDoc(doc(db, collectionPath('orders'), orderid))
                );
                
                console.log('Order doc exists:', orderDoc.exists());
                
                if (!orderDoc.exists()) {
                    console.error('Order document not found in Firestore. Document ID:', orderid);
                    setToast({ message: 'Order not found', type: 'error' });
                    setIsAuthorized(false);
                    setLoading(false);
                    return;
                }
                
                const orderData = { id: orderDoc.id, ...orderDoc.data() };
                console.log('Order data:', orderData);
                
                // Authorization check: Admin OR user in commissions OR assigned to customer
                const canViewAll = can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL);
                const userMatchNames = new Set(getRbacMatchNames(currentUser));
                
                // Check if user is in commissions_displayNames
                const isInCommissions = Array.isArray(orderData.commissions_displayNames) && 
                    orderData.commissions_displayNames.some(name =>
                        name && userMatchNames.has(name.toLowerCase().trim())
                    );
                
                console.log('Authorization check:', {
                    canViewAll,
                    userMatchNames: [...userMatchNames],
                    commissions_displayNames: orderData.commissions_displayNames,
                    isInCommissions
                });
                
                // Check if user is assigned to the customer
                let isAssignedToCustomer = false;
                if (orderData.customer_id) {
                    try {
                        let customersRef;
                        if (orderData.customerAccountNumber) {
                            customersRef = await monitorQuery('fetch-customer-for-order-auth-account', () =>
                                getDocs(query(
                                    collection(db, collectionPath('customers')),
                                    where('customerAccountNumber', '==', String(orderData.customerAccountNumber)),
                                    limit(1)
                                ))
                            );
                        }

                        // Older orders may lack the enriched account number; customer_id is AM's internal ID.
                        if ((!customersRef || customersRef.empty) && orderData.customer_id) {
                            customersRef = await monitorQuery('fetch-customer-for-order-auth-am-id', () =>
                                getDocs(query(
                                    collection(db, collectionPath('customers')),
                                    where('apparelMagicId', '==', String(orderData.customer_id)),
                                    limit(1)
                                ))
                            );
                        }
                        
                        if (!customersRef.empty) {
                            const customerData = customersRef.docs[0].data();
                            console.log('Customer data:', customerData);
                            
                            // Check if user is in salesRepNames array
                            if (Array.isArray(customerData.salesRepNames)) {
                                isAssignedToCustomer = customerData.salesRepNames.some(name =>
                                    name && userMatchNames.has(name.toLowerCase().trim())
                                );
                            }

                            if (!isAssignedToCustomer && Array.isArray(customerData.salesReps)) {
                                isAssignedToCustomer = customerData.salesReps.some(rep =>
                                    rep?.salesRepName && userMatchNames.has(rep.salesRepName.toLowerCase().trim())
                                );
                            }
                            
                            console.log('Customer check:', {
                                salesRepNames: customerData.salesRepNames,
                                salesReps: customerData.salesReps,
                                userMatchNames: [...userMatchNames],
                                isAssignedToCustomer
                            });
                        }
                    } catch (err) {
                        console.error('Error fetching customer:', err);
                    }
                }
                
                const authorized = canViewAll || isInCommissions || isAssignedToCustomer;
                console.log('Final authorization result:', authorized);
                
                if (authorized) {
                    setOrder(orderData);
                    setIsAuthorized(true);
                } else {
                    console.warn('User not authorized to view this order');
                    setOrder(null);
                    setIsAuthorized(false);
                    setToast({ 
                        message: 'You are not authorized to view this order. Only assigned sales representatives can access this order.', 
                        type: 'error' 
                    });
                }
            } catch (error) {
                console.error('Error fetching order. Order ID:', orderid, 'Error:', error);
                console.error('Error details:', error.message, error.code);
                setToast({ message: `Error loading order details: ${error.message}`, type: 'error' });
                setIsAuthorized(false);
            } finally {
                setLoading(false);
            }
        };

        fetchOrder();
    }, [currentUser, orderid, can]);

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return '$0.00';
        return `$${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return dateStr;
    };

    const formatDateTime = (timestamp) => {
        if (!timestamp) return '-';
        // Handle Firestore Timestamp objects
        if (timestamp && typeof timestamp === 'object' && timestamp.toDate) {
            return formatDateHelper(timestamp.toDate(), 'MM/DD/YYYY h:mm A');
        }
        // Handle date strings
        if (typeof timestamp === 'string') {
            return timestamp;
        }
        return '-';
    };

    if (loading) {
        return (
            <main className="flex-grow p-6 overflow-y-auto">
                <div className="flex items-center justify-center h-64">
                    <div className="text-gray-500">Loading order details...</div>
                </div>
            </main>
        );
    }

    if (!order || !isAuthorized) {
        return (
            <main className="flex-grow p-6 overflow-y-auto">
                {toast && <Toast {...toast} onClose={() => setToast(null)} />}
                <div className="flex flex-col items-center justify-center h-64">
                    <div className="text-center">
                        <div className="text-red-600 text-6xl mb-4">🔒</div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            {!isAuthorized ? 'Access Denied' : 'Order Not Found'}
                        </h2>
                        <p className="text-gray-500 mb-4">
                            {!isAuthorized 
                                ? 'You do not have permission to view this order. Only assigned sales representatives can access order details.' 
                                : 'The order you are looking for does not exist.'}
                        </p>
                        <button
                            onClick={() => navigate(-1)}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                        >
                            ← Go Back
                        </button>
                    </div>
                </div>
            </main>
        );
    }

    return (
        <main className="flex-grow p-6 overflow-y-auto">
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}

            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="text-indigo-600 hover:text-indigo-500"
                    >
                        ← Back
                    </button>
                    <h1 className="text-2xl font-bold text-gray-900">
                        Order #{order.order_id || order.id}
                    </h1>
                    <span className={`px-3 py-1 text-sm font-semibold rounded-full ${
                        order.fulfillment_status === 'Open' ? 'bg-blue-100 text-blue-700' :
                        order.fulfillment_status === 'Shipped' ? 'bg-green-100 text-green-700' :
                        order.fulfillment_status === 'Delivered' ? 'bg-green-100 text-green-700' :
                        order.fulfillment_status === 'Cancelled' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                    }`}>
                        {order.fulfillment_status || 'Unknown'}
                    </span>
                </div>
            </div>

            {/* Order Summary */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h2 className="text-lg font-bold mb-4">Order Summary</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <div className="mb-3">
                            <span className="text-sm text-gray-500">Order Number</span>
                            <div className="font-semibold text-lg">{order.order_id}</div>
                        </div>
                        <div className="mb-3">
                            <span className="text-sm text-gray-500">Customer PO</span>
                            <div className="font-medium">{order.customer_po || '-'}</div>
                        </div>
                        <div className="mb-3">
                            <span className="text-sm text-gray-500">Date Created</span>
                            <div className="font-medium">{formatDate(order.date_created || order.date_internal || order.date)}</div>
                        </div>
                        <div className="mb-3">
                            <span className="text-sm text-gray-500">Order Date</span>
                            <div className="font-medium">{formatDate(order.date)}</div>
                        </div>
                    </div>
                    <div>
                        <div className="mb-3">
                            <span className="text-sm text-gray-500">Due Date</span>
                            <div className="font-medium">{formatDate(order.date_due)}</div>
                        </div>
                        <div className="mb-3">
                            <span className="text-sm text-gray-500">Start Date</span>
                            <div className="font-medium">{formatDate(order.date_start)}</div>
                        </div>
                        <div className="mb-3">
                            <span className="text-sm text-gray-500">Season</span>
                            <div className="font-medium">{order.season || '-'}</div>
                        </div>
                    </div>
                    <div>
                        <div className="mb-3">
                            <span className="text-sm text-gray-500">Total Amount</span>
                            <div className="font-bold text-xl text-indigo-600">{formatCurrency(order.amount)}</div>
                        </div>
                        <div className="mb-3">
                            <span className="text-sm text-gray-500">Balance</span>
                            <div className="font-semibold">{formatCurrency(order.balance)}</div>
                        </div>
                        <div className="mb-3">
                            <span className="text-sm text-gray-500">Credit Status</span>
                            <div className={`font-medium ${order.credit_status === 'Approved' ? 'text-green-600' : 'text-gray-700'}`}>
                                {order.credit_status || '-'}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Customer Information */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h2 className="text-lg font-bold mb-4">Customer Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <div className="mb-3">
                            <span className="text-sm text-gray-500">Customer Name</span>
                            <div className="font-semibold">{order.customer_name}</div>
                        </div>
                        <div className="mb-3">
                            <span className="text-sm text-gray-500">Customer ID</span>
                            <div className="font-medium">{order.customer_id}</div>
                        </div>
                        <div className="mb-3">
                            <span className="text-sm text-gray-500">Account Number</span>
                            <div className="font-medium">{order.account_number || '-'}</div>
                        </div>
                    </div>
                    <div>
                        <div className="mb-3">
                            <span className="text-sm text-gray-500">Email</span>
                            <div className="font-medium">{order.email || '-'}</div>
                        </div>
                        <div className="mb-3">
                            <span className="text-sm text-gray-500">Phone</span>
                            <div className="font-medium">{order.phone || '-'}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Shipping Address */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h2 className="text-lg font-bold mb-4">Shipping Address</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <div className="mb-2">
                            <span className="font-medium">{order.address_1}</span>
                        </div>
                        {order.address_2 && (
                            <div className="mb-2">
                                <span className="font-medium">{order.address_2}</span>
                            </div>
                        )}
                        <div className="mb-2">
                            <span className="font-medium">
                                {order.city}, {order.state} {order.postal_code}
                            </span>
                        </div>
                        <div className="mb-2">
                            <span className="font-medium">{order.country}</span>
                        </div>
                    </div>
                    <div>
                        <div className="mb-3">
                            <span className="text-sm text-gray-500">Ship Via</span>
                            <div className="font-medium">{order.ship_via || '-'}</div>
                        </div>
                        <div className="mb-3">
                            <span className="text-sm text-gray-500">Warehouse</span>
                            <div className="font-medium">{order.warehouse_id || '-'}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sales & Commission Information */}
            {(order.commissions || order.commissions_displayNames) && (
                <div className="bg-white rounded-lg shadow p-6 mb-6">
                    <h2 className="text-lg font-bold mb-4">Sales Representatives</h2>
                    <div className="space-y-3">
                        {Array.isArray(order.commissions) && order.commissions.length > 0 ? (
                            order.commissions.map((commission, idx) => (
                                <div key={idx} className="border-b pb-3 last:border-b-0">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <div className="font-semibold">{commission.displayName || commission.name}</div>
                                            <div className="text-sm text-gray-500">Rep ID: {commission.id}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-semibold">{formatCurrency(commission.amount_commission)}</div>
                                            <div className="text-sm text-gray-500">{commission.rate}% rate</div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : Array.isArray(order.commissions_displayNames) && order.commissions_displayNames.length > 0 ? (
                            order.commissions_displayNames.map((name, idx) => (
                                <div key={idx} className="font-medium">{name}</div>
                            ))
                        ) : (
                            <div className="text-gray-500">No sales representatives assigned</div>
                        )}
                    </div>
                </div>
            )}

            {/* Financial Details */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h2 className="text-lg font-bold mb-4">Financial Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <div className="flex justify-between mb-2">
                            <span className="text-gray-600">Subtotal</span>
                            <span className="font-medium">{formatCurrency(order.amount_subtotal)}</span>
                        </div>
                        <div className="flex justify-between mb-2">
                            <span className="text-gray-600">Discount ({order.pct_discount}%)</span>
                            <span className="font-medium text-red-600">-{formatCurrency(order.amount_discount)}</span>
                        </div>
                        <div className="flex justify-between mb-2">
                            <span className="text-gray-600">Freight</span>
                            <span className="font-medium">{formatCurrency(order.amount_freight)}</span>
                        </div>
                        <div className="flex justify-between mb-2">
                            <span className="text-gray-600">Tax</span>
                            <span className="font-medium">{formatCurrency(order.amount_tax)}</span>
                        </div>
                        <div className="flex justify-between mb-2">
                            <span className="text-gray-600">Misc Charges</span>
                            <span className="font-medium">{formatCurrency(order.amount_misc)}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t font-bold">
                            <span>Total Amount</span>
                            <span className="text-indigo-600">{formatCurrency(order.amount)}</span>
                        </div>
                    </div>
                    <div>
                        <div className="flex justify-between mb-2">
                            <span className="text-gray-600">Amount Paid</span>
                            <span className="font-medium text-green-600">{formatCurrency(order.amount_paid)}</span>
                        </div>
                        <div className="flex justify-between mb-2">
                            <span className="text-gray-600">Amount Open</span>
                            <span className="font-medium">{formatCurrency(order.amount_open)}</span>
                        </div>
                        <div className="flex justify-between mb-2">
                            <span className="text-gray-600">Amount Shipped</span>
                            <span className="font-medium">{formatCurrency(order.amount_shipped)}</span>
                        </div>
                        <div className="flex justify-between mb-2">
                            <span className="text-gray-600">Amount Allocated</span>
                            <span className="font-medium">{formatCurrency(order.amount_alloc)}</span>
                        </div>
                        <div className="flex justify-between mb-2">
                            <span className="text-gray-600">Amount Cancelled</span>
                            <span className="font-medium text-red-600">{formatCurrency(order.amount_cxl)}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t font-bold">
                            <span>Balance Due</span>
                            <span className="text-red-600">{formatCurrency(order.balance)}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Order Items */}
            {Array.isArray(order.order_items) && order.order_items.length > 0 && (
                <div className="bg-white rounded-lg shadow p-6 mb-6">
                    <h2 className="text-lg font-bold mb-4">Order Items ({order.order_items.length})</h2>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b">
                                <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase">
                                    <th className="px-4 py-3">Item #</th>
                                    <th className="px-4 py-3">Style / SKU</th>
                                    <th className="px-4 py-3">Description</th>
                                    <th className="px-4 py-3">Size</th>
                                    <th className="px-4 py-3">Qty</th>
                                    <th className="px-4 py-3">Unit Price</th>
                                    <th className="px-4 py-3">Amount</th>
                                    <th className="px-4 py-3">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {order.order_items.map((item, idx) => (
                                    <tr key={item.id || idx} className="text-sm">
                                        <td className="px-4 py-3">{item.row_id || idx + 1}</td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium">{item.style_number}</div>
                                            <div className="text-xs text-gray-500">{item.sku_alt || item.sku_id}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div>{item.description}</div>
                                            {item.attr_2 && <div className="text-xs text-gray-500">Color: {item.attr_2}</div>}
                                            {item.attr_3 && <div className="text-xs text-gray-500">{item.attr_3}</div>}
                                        </td>
                                        <td className="px-4 py-3">{item.size}</td>
                                        <td className="px-4 py-3">
                                            <div>Ordered: {item.qty}</div>
                                            {parseFloat(item.qty_shipped) > 0 && (
                                                <div className="text-xs text-green-600">Shipped: {item.qty_shipped}</div>
                                            )}
                                            {parseFloat(item.qty_open) > 0 && (
                                                <div className="text-xs text-blue-600">Open: {item.qty_open}</div>
                                            )}
                                            {parseFloat(item.qty_cxl) > 0 && (
                                                <div className="text-xs text-red-600">Cancelled: {item.qty_cxl}</div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">{formatCurrency(item.unit_price)}</td>
                                        <td className="px-4 py-3 font-semibold">{formatCurrency(item.amount)}</td>
                                        <td className="px-4 py-3">
                                            {parseFloat(item.qty_shipped) >= parseFloat(item.qty) ? (
                                                <span className="text-green-600 text-xs">Shipped</span>
                                            ) : parseFloat(item.qty_cxl) >= parseFloat(item.qty) ? (
                                                <span className="text-red-600 text-xs">Cancelled</span>
                                            ) : (
                                                <span className="text-blue-600 text-xs">Open</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Additional Information */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h2 className="text-lg font-bold mb-4">Additional Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <div className="mb-3">
                            <span className="text-sm text-gray-500">Division</span>
                            <div className="font-medium">{order.division_name || '-'}</div>
                        </div>
                        <div className="mb-3">
                            <span className="text-sm text-gray-500">Currency</span>
                            <div className="font-medium">{order.currency_name || 'USD'} (Rate: {order.currency_rate})</div>
                        </div>
                        <div className="mb-3">
                            <span className="text-sm text-gray-500">Payment Terms</span>
                            <div className="font-medium">{order.terms_id || '-'}</div>
                        </div>
                        <div className="mb-3">
                            <span className="text-sm text-gray-500">Priority</span>
                            <div className="font-medium">{order.priority_text || order.priority || '-'}</div>
                        </div>
                    </div>
                    <div>
                        <div className="mb-3">
                            <span className="text-sm text-gray-500">Created By</span>
                            <div className="font-medium">{order.creation_user_name}</div>
                            <div className="text-xs text-gray-500">{formatDateTime(order.creation_time)}</div>
                        </div>
                        <div className="mb-3">
                            <span className="text-sm text-gray-500">Last Modified By</span>
                            <div className="font-medium">{order.last_modified_user_name}</div>
                            <div className="text-xs text-gray-500">{formatDateTime(order.last_modified_time)}</div>
                        </div>
                        <div className="mb-3">
                            <span className="text-sm text-gray-500">Is Quote</span>
                            <div className="font-medium">{order.is_quote === '1' ? 'Yes' : 'No'}</div>
                        </div>
                        <div className="mb-3">
                            <span className="text-sm text-gray-500">Is Locked</span>
                            <div className="font-medium">{order.is_locked === '1' ? 'Yes' : 'No'}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Notes */}
            {(order.notes || order.private_notes || order.production_notes) && (
                <div className="bg-white rounded-lg shadow p-6 mb-6">
                    <h2 className="text-lg font-bold mb-4">Notes</h2>
                    {order.notes && (
                        <div className="mb-3">
                            <span className="text-sm font-semibold text-gray-700">Order Notes</span>
                            <div className="mt-1 p-3 bg-gray-50 rounded">{order.notes}</div>
                        </div>
                    )}
                    {order.private_notes && (
                        <div className="mb-3">
                            <span className="text-sm font-semibold text-gray-700">Private Notes</span>
                            <div className="mt-1 p-3 bg-yellow-50 rounded">{order.private_notes}</div>
                        </div>
                    )}
                    {order.production_notes && (
                        <div className="mb-3">
                            <span className="text-sm font-semibold text-gray-700">Production Notes</span>
                            <div className="mt-1 p-3 bg-blue-50 rounded">{order.production_notes}</div>
                        </div>
                    )}
                </div>
            )}

            {/* System Information */}
            <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-bold mb-4">System Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                        <span className="text-gray-500">Order ID (ApparelMagic):</span>
                        <div className="font-medium">{order.order_id}</div>
                    </div>
                    <div>
                        <span className="text-gray-500">Firestore Document ID:</span>
                        <div className="font-medium font-mono text-xs">{order.id}</div>
                    </div>
                    <div>
                        <span className="text-gray-500">Last Synced:</span>
                        <div className="font-medium">{formatDateTime(order.lastUpdated || order.importedOn)}</div>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default OrderDetailPage;





